import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, CheckCircle, AlertCircle, FileText, Info } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { tenantIdForStoredKey, tenantIdsEqual } from '@/lib/tenantUtils';

export default function IncumbentSentimentUploader() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv') || selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.ms-excel';
    
    if (isCSV) {
      setFile(selectedFile);
      setError(null);
      setResults(null);
    } else {
      setError('Please select a valid CSV file (must end with .csv)');
      setFile(null);
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain a header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    const requiredColumns = ['tenant_id', 'account_name', 'incumbent_sentiment', 'sentiment_rationale'];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1} has incorrect number of columns, skipping`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index]?.trim() || '';
      });

      if (row.account_name) {
        data.push(row);
      }
    }

    return data;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResults(null);

    try {
      const text = await file.text();
      const data = parseCSV(text);

      console.log('📊 Parsed CSV data:', data);

      const allAccounts = await AccountFinal.list();
      
      let updated = 0;
      let notFound = 0;
      const errors = [];

      for (const row of data) {
        try {
          const targetTenantId = tenantIdForStoredKey(row.tenant_id);
          if (!targetTenantId) {
            errors.push(`Missing tenant_id for account "${row.account_name || '(blank)'}"`);
            continue;
          }
          const accountNameLower = String(row.account_name || '').toLowerCase().trim();
          if (!accountNameLower) {
            errors.push('Row missing account_name');
            continue;
          }
          
          // Find account by name - it might have a different tenant_id
          const account = allAccounts.find(acc => 
            acc.account_name?.toLowerCase().trim() === accountNameLower
          );

          if (!account) {
            notFound++;
            errors.push(`Account "${row.account_name}" not found in any tenant`);
            console.warn(`❌ Account not found: ${row.account_name}`);
            continue;
          }

          console.log(`🔍 Found account "${account.account_name}" with tenant_id: ${account.tenant_id}, updating for tenant: ${targetTenantId}`);

          // Determine if we need to use tenant_overrides or direct update
          const isOwnTenant = tenantIdsEqual(account.tenant_id, targetTenantId);
          
          if (isOwnTenant) {
            // Direct update: this tenant owns the account
            console.log(`✅ Direct update for ${account.account_name} (owned by ${targetTenantId})`);
            await AccountFinal.update(account.id, {
              incumbent_sentiment: row.incumbent_sentiment || null,
              incumbent_sentiment_rationale: row.sentiment_rationale || null
            });
          } else {
            // Tenant override: this tenant has visibility but doesn't own the account
            console.log(`🔄 Tenant override for ${account.account_name} (owned by ${account.tenant_id}, override for ${targetTenantId})`);
            
            const currentOverrides = account.tenant_overrides || {};
            const tenantOverride = {
              ...(currentOverrides[targetTenantId] || {}),
              incumbent_sentiment: row.incumbent_sentiment || null,
              incumbent_sentiment_rationale: row.sentiment_rationale || null
            };
            
            await AccountFinal.update(account.id, {
              tenant_overrides: {
                ...currentOverrides,
                [targetTenantId]: tenantOverride
              }
            });
          }

          updated++;
        } catch (err) {
          console.error(`Error updating account ${row.account_name}:`, err);
          errors.push(`Failed to update ${row.account_name}: ${err.message}`);
        }
      }

      setResults({
        total: data.length,
        updated,
        notFound,
        errors
      });

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'tenant_id,account_name,incumbent_sentiment,sentiment_rationale\ninstructure,University of Sydney,Satisfied,The institution has expressed strong satisfaction with their current LMS provider and renewed their contract for 3 more years.\nsmowl,University of Melbourne,Exploring alternatives,Recent discussions indicate frustration with current vendor support and they are actively evaluating competitors.';
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'incumbent_sentiment_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00C2C2]" />
          Incumbent Sentiment Bulk Uploader
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            <strong>Multi-tenant support:</strong> When uploading data for a tenant that doesn't own the account (e.g., smowl viewing instructure accounts), 
            the data is stored as a tenant override. This allows each tenant to have their own view of incumbent sentiment without affecting other tenants.
          </AlertDescription>
        </Alert>

        <div className="text-sm text-gray-600">
          <p className="mb-2">Upload a CSV file to bulk update incumbent sentiment data for accounts.</p>
          <p className="mb-2 font-semibold">Required columns:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">tenant_id</code> - The tenant identifier (e.g., instructure, smowl, clairelabs)</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">account_name</code> - Exact account name as it appears in the system</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">incumbent_sentiment</code> - Sentiment label (e.g., Satisfied, Frustrated, Exploring alternatives)</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">sentiment_rationale</code> - Detailed explanation (can be long text)</li>
          </ul>
        </div>

        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="w-full"
        >
          <Download className="w-4 h-4 mr-2" />
          Download CSV Template
        </Button>

        <div className="border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-6 text-center transition-colors">
          <input
            type="file"
            accept=".csv,text/csv,application/vnd.ms-excel"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload-incumbent"
          />
          <label htmlFor="csv-upload-incumbent" className="cursor-pointer block">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              {file ? '✓ File selected' : 'Click to select CSV file'}
            </p>
            {file && (
              <p className="text-xs text-gray-500">{file.name}</p>
            )}
            {!file && (
              <p className="text-xs text-gray-500">or drag and drop</p>
            )}
          </label>
        </div>

        {file && (
          <Button
            onClick={handleUpload}
            disabled={isProcessing}
            className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
          >
            {isProcessing ? 'Processing...' : 'Upload and Process'}
          </Button>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {results && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold text-green-800">Upload Complete</p>
                <div className="text-sm text-green-700">
                  <p>Total records: {results.total}</p>
                  <p>Successfully updated: {results.updated}</p>
                  <p>Not found: {results.notFound}</p>
                </div>
                {results.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-orange-700">Errors:</p>
                    <ul className="text-xs text-orange-600 list-disc list-inside">
                      {results.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {results.errors.length > 5 && (
                        <li>... and {results.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}